import { Injectable, OnDestroy } from '@angular/core';
import { DevicesStoreFacade, InputAppClientDBFacade } from '@monorepo/core-data';
import { RfidTagReaderService, TScanModes } from '../../input-app/rfid-tag-reader.service';
import { BleService } from '../bluetooth/ble.service';
import { distinctUntilChanged, map, skip, take } from 'rxjs/operators';
import { HIDService } from '../bluetooth/hid.service';
import { TRfidDevice } from '@monorepo/types';
import { InputAppAPIHelperUtil } from '@monorepo/utils';
import { merge, Subscription } from 'rxjs';
import { ZebraBleModeReaderService } from '../../input-app/zebra-ble-mode/zebra-ble-mode-reader.service';
import { NFCReaderService } from '../nfc/nfc-reader.service';

@Injectable({
  providedIn: 'root',
})
export class DevicesService implements OnDestroy {
  rfidScanMode: TScanModes = 'multi_tag';
  #isBleDeviceConnected = false;
  #connectedRfidDevice: TRfidDevice | null = null;
  #isNFCConnected: boolean = false;
  #isZebraBleDeviceConnected = false;

  subscriptions$: Subscription[] = [];

  constructor(
    private devicesStoreFacade: DevicesStoreFacade,
    private bleService: BleService,
    private inputAppClientDBFacade: InputAppClientDBFacade,
    private hidService: HIDService,
    private apiHelper: InputAppAPIHelperUtil,
    private rfidTagReaderService: RfidTagReaderService,
    private zebraBleModeReaderService: ZebraBleModeReaderService,
    private nfcService: NFCReaderService
  ) {
    const s1$ = this.rfidTagReaderService
      .getRFIDConnectionStateEmitterSubscription()
      .pipe(skip(1))
      .subscribe((res) => {
        const { vendor, deviceId, deviceName, readPower } = res;
        if (res.connectionState === 'CONNECTED')
          this.apiHelper.showFailureToast(`Connected to ${vendor} device.`, true);
        else this.apiHelper.showFailureToast(`${vendor} device disconnected!`, true);
        const deviceInfo: TRfidDevice = { vendor, deviceId, deviceName, readPower };
        this.devicesStoreFacade.updateConnectedDeviceInfo({
          connectionStatus: res.connectionState,
          connectionType: 'RFID',
          deviceInfo,
        });
      });

    const s2$ = this.nfcService.getNFCConnectionStateEmitterSubscription().pipe(skip(1)).subscribe((res) => {
      if (res.connectionState === 'CONNECTED')
          this.apiHelper.showFailureToast(`Connected to NFC device`, true)
      else this.apiHelper.showFailureToast(`NFC device disconnected!`, true);

      this.devicesStoreFacade.updateConnectedDeviceInfo({
        connectionStatus: res.connectionState,
        connectionType: 'NFC',
        deviceInfo: { vendor: "NFC", deviceName: "NFC" },
      });
    })

    const s3$ = this.devicesStoreFacade.getRfidScanMode().subscribe((scanMode) => (this.rfidScanMode = scanMode));

    const s4$ = this.rfidTagReaderService
      .getReadpowerUpdateEmitterSubscription()
      .pipe(skip(1))
      .subscribe((res) => {
        const { oldReadpower, newReadpower } = res;
        if (this.#connectedRfidDevice) {
          this.apiHelper.showFailureToast(`Read power updated from ${oldReadpower} to ${newReadpower}`, true);
          this.devicesStoreFacade.updateConnectedDeviceInfo({
            connectionStatus: 'CONNECTED',
            connectionType: 'RFID',
            deviceInfo: {
              ...this.#connectedRfidDevice,
              readPower: newReadpower,
            },
          });
        }
      });

    const s5$ = this.zebraBleModeReaderService
      .getZebraBleConnectionStateEmitterSubscription()
      .pipe(
        distinctUntilChanged((a, b) => a.connectionState === b.connectionState),
        skip(1),
      )
      .subscribe((status) => {
        this.#isZebraBleDeviceConnected = status.connectionState === 'CONNECTED';
        this.apiHelper.showFailureToast(`Zebra Scanner ${status.connectionState}`, true);
      });
    this.subscriptions$.push(s1$, s2$, s3$, s4$, s5$);
  }

  ngOnDestroy(): void {
    this.subscriptions$.forEach((s) => s.unsubscribe());
  }

  initDevicesStore(window: Window, appName: 'ba-app' | 'input-app' = 'input-app') {
    // Init devices db
    this.hidService.init(window);
    const s3$ = this.devicesStoreFacade.getInitBleServiceSubscription().subscribe((bleModeInitDTO) => {
      // -- 1 Subscription
      const s4$ = this.devicesStoreFacade.getDevicesDBFromCache().subscribe((res) => {
        this.bleService.initBle(bleModeInitDTO);

        // Only one out of vendor based RFID or NFC would be connected
        if (res.connectedDeviceInfo.RFID) {
          this.rfidTagReaderService.connectToVendorBasedRfidDevice(
            res.connectedDeviceInfo.RFID,
            res.connectedDeviceInfo.scanMode,
          );
        } else if (res.connectedDeviceInfo.NFC) {
          this.nfcService.connectNFC(res.connectedDeviceInfo.scanMode);
        }

      });
      this.subscriptions$.push(s4$);
    });

    const s4$ = this.devicesStoreFacade.getConnectedDeviceInfo().subscribe((devices) => {
      this.#isBleDeviceConnected = !!devices.BLUETOOTH;
      this.#connectedRfidDevice = devices.RFID;
      this.#isNFCConnected = !!devices.NFC;
    });

    // TODO [OKR]
    switch (appName) {
      case 'ba-app': {
        this.devicesStoreFacade.initDevicesDB({ serviceId: '', characteristicId: '' });
        break;
      }
      case 'input-app': {
        this.inputAppClientDBFacade // -- 1
          .getConfigurations()
          .pipe(take(1))
          .subscribe(({ serviceId, characteristicId }) => {
            this.devicesStoreFacade.initDevicesDB({ serviceId, characteristicId });
          });
        break;
      }
    }
    this.subscriptions$.push(s3$, s4$);
  }

  connectToRfidDevice(device: TRfidType) {
    if(device.type === "NFC") {
      this.#connectToNFCDevice();
    } else {
      const { vendor, readPower } = device;
      this.#connectToVendorBasedRfidDevice(vendor, readPower)
    }
  }

  async #connectToNFCDevice() {
    const connected = await this.nfcService.connectNFC(this.rfidScanMode);
    if(!connected) {
      this.apiHelper.showFailureToast("Can't connect to NFC, either NFC is not available or permission is denied!", true);
    }
  }

  #connectToVendorBasedRfidDevice(vendor: TRfidDevice['vendor'], readPower: number) {
    const { minimumSupportedRange, maximumSupportedRange }
      = this.getMinimumAndMaximumSupportedRangeForTheVendorBasedRfidDevice(vendor);
    if (readPower < minimumSupportedRange || readPower > maximumSupportedRange) readPower = maximumSupportedRange;

    let deviceInfo: TRfidDevice | null = null;
    switch (vendor) {
      case 'NEWLAND': {
        deviceInfo = {
          vendor: 'NEWLAND',
          deviceId: '',
          deviceName: 'URF100',
          readPower,
        };
        break;
      }
      case 'CHAINWAY': {
        deviceInfo = {
          vendor: 'CHAINWAY',
          deviceName: 'P80',
          readPower,
          deviceId: '',
        };
        break;
      }
      case 'APULSETECH': {
        deviceInfo = {
          vendor: 'APULSETECH',
          deviceName: '',
          deviceId: '',
          readPower,
        };
        break;
      }
    }
    if (!deviceInfo) return false;
    this.rfidTagReaderService.connectToVendorBasedRfidDevice(deviceInfo as TRfidDevice, this.rfidScanMode);
    return true;
  }

  async disconnectRfidDevice(type: "NFC" | "VENDOR") {
    if(type === "NFC") {
      await this.#disconnectNFCDevice();
    } else {
      await this.#disconnectVendorBasedRfidDevice();
    }
  }

  async #disconnectNFCDevice() {
    await this.nfcService.disconnectNFC();
  }

  async #disconnectVendorBasedRfidDevice() {
    const res = await this.rfidTagReaderService.disconnectDevice();
    if (!res.status) this.apiHelper.showFailureToast('Something went wrong while disconnecting device!', true);
  }

  startTagRead() {
    if(!this.isAnyRfidDeviceConnected()) {
      this.apiHelper.showFailureToast('No RFID/NFC device connected', true);
      return;
    }
    if(this.#connectedRfidDevice) {
      this.rfidTagReaderService.startRead();
    } else if(this.#isNFCConnected) {
      this.nfcService.startNFCRead();
    }
  }

  stopTagRead() {
    if(!this.isAnyRfidDeviceConnected()) return;
    if(this.#connectedRfidDevice) {
      this.rfidTagReaderService.stopRead();
    } else if(this.#isNFCConnected) {
      this.nfcService.stopNFCRead();
    }
  }

  async updateRfidScanMode(scanMode: TScanModes) {
    if(this.#connectedRfidDevice) await this.rfidTagReaderService.updateScanMode(scanMode);
    if(this.#isNFCConnected) await this.nfcService.updateScanMode(scanMode);
    this.devicesStoreFacade.updateRfidScanMode(scanMode);
    return true; // we are just updating the variable in plugin, so it would be a success everytime.
  }

  getConnectedDeviceInfo() {
    return this.devicesStoreFacade.getConnectedDeviceInfo();
  }

  getConnectedRfidOrNfcDevice() {
    return this.getConnectedDeviceInfo().pipe(
      map(({ RFID, NFC }) => RFID ?? NFC),
      distinctUntilChanged(),
    );
  }

  getRfidScanMode() {
    return this.devicesStoreFacade.getRfidScanMode().pipe(
      distinctUntilChanged(),
      map((scanMode) => scanMode)
    );
  }

  getRfidConnectionStatusSubscription() {
    return this.getConnectedRfidOrNfcDevice().pipe(
      map((device) => (device ? 'CONNECTED' : 'DISCONNECTED'))
    );
  }

  getScannedRfidTagSubscription() {
    return merge(
      this.rfidTagReaderService.getScannedRFIDTagsEmitterSubscription(),
      this.nfcService.getNFCScannedDataSubscription()
    ).pipe(map((data) => data.tagList));
  }

  isAnyRfidDeviceConnected() {
    return (!!this.#connectedRfidDevice) || this.#isNFCConnected;
  }

  isAnyDeviceConnected(): boolean {
    // return this.#isBleDeviceConnected || !!this.#connectedRfidDevice || this.#isZebraBleDeviceConnected;
    // TODO - AMAN [PBI-2847] setting it to true to unblock the users until we find the root cause for this issue
    return true;
  }

  // readpower is only available for vendor based UHF RFID readers
  updateReadpower(readpower: number) {
    this.rfidTagReaderService.updateReadpower(readpower);
  }

  setZebraEnabled(enabled: boolean) {
    void this.zebraBleModeReaderService.setZebraEnabled(enabled);
  }

  connectToZebraScannerUsingBleMode() {
    void this.zebraBleModeReaderService.showConnectDeviceBarcode();
  }

  getZebraBleModeDeviceConnectionSubscription() {
    return this.zebraBleModeReaderService.getZebraBleConnectionStateEmitterSubscription();
  }

  disconnectZebraBleModeDevice() {
    this.zebraBleModeReaderService.disconnectZebraBleModeDevice();
  }

  getMinimumAndMaximumSupportedRangeForTheVendorBasedRfidDevice(vendor: TRfidDevice['vendor']): {
    minimumSupportedRange: number;
    maximumSupportedRange: number;
  } {
    switch (vendor) {
      case 'APULSETECH': {
        return {
          minimumSupportedRange: 0,
          maximumSupportedRange: 30,
        };
      }
      case 'CHAINWAY': {
        return {
          minimumSupportedRange: 1,
          maximumSupportedRange: 30,
        };
      }
      case 'NEWLAND': {
        return {
          minimumSupportedRange: 1,
          maximumSupportedRange: 30,
        };
      }
    }
  }
}

type TRfidType = {
  type: "VENDOR",
  vendor: TRfidDevice['vendor'],
  readPower: number 
} | { type: "NFC" }
